import { PaginationRequest } from 'app/business/common/paginationrequest';
import { MasterService } from 'app/service/master.service';

export interface FssaiNutrientOption {
  id: string;
  name: string;
  nutrientType?: string;
  itemUomMasterId?: number | string | null;
  uomName?: string | null;
}

export interface FssaiNutrientTagOption {
  id: string;
  name: string;
  fssaiTagName?: string;
  nutrientId?: number | string;
  condition?: string;
  stateOfFood?: string;
  uomName?: string;
  basis?: string;
}

export type FssaiNutrientTagRow = {
  nutrientId: string | null;
  selectedTagIds: string[];
  availableTags: FssaiNutrientTagOption[];
  isLoadingTags: boolean;
};

function normalizeNutrientType(value: string | null | undefined): string {
  return String(value ?? '')
    .toUpperCase()
    .replace(/[\s_-]/g, '');
}

export function isFssaiMacroNutrient(nutrientType: string | null | undefined): boolean {
  const type = normalizeNutrientType(nutrientType);
  return type.includes('MACRO');
}

export function isFssaiMicroNutrient(nutrientType: string | null | undefined): boolean {
  const type = normalizeNutrientType(nutrientType);
  return type.includes('MICRO');
}

export function extractFssaiNutrientList(response: any): FssaiNutrientOption[] {
  const list =
    response?.response?.nutrientList ??
    response?.response?.items ??
    response?.nutrientList ??
    [];

  return (Array.isArray(list) ? list : [])
    .filter((item: any) => item?.isActive !== false && item?.isDeleted !== true)
    .map((item: any) => ({
      id: String(item.id ?? item.legacyId ?? ''),
      name: item.name ?? item.description ?? '',
      nutrientType: item.nutrientType,
      itemUomMasterId: item.itemUomMasterId ?? null,
      uomName: item.uomName ?? null,
    }))
    .filter((item) => !!item.id && !!item.name);
}

export function extractFssaiNutrientTagList(response: any): FssaiNutrientTagOption[] {
  const list =
    response?.response?.tagList ??
    response?.response?.items ??
    response?.tagList ??
    [];

  return (Array.isArray(list) ? list : [])
    .filter((item: any) => item?.isActive !== false)
    .map((item: any) => ({
      id: String(item.id ?? item.legacyId ?? ''),
      name: item.fssaiTagName ?? item.name ?? '',
      fssaiTagName: item.fssaiTagName ?? item.name ?? '',
      nutrientId: item.nutrientId,
      condition: item.condition,
      stateOfFood: item.stateOfFood,
      uomName: item.uomName,
      basis: item.basis,
    }))
    .filter((item) => !!item.id && !!item.name);
}

export function splitFssaiNutrientsByType(nutrients: FssaiNutrientOption[]): {
  macro: FssaiNutrientOption[];
  micro: FssaiNutrientOption[];
} {
  const macro = nutrients.filter((item) => isFssaiMacroNutrient(item.nutrientType));
  const micro = nutrients.filter((item) => isFssaiMicroNutrient(item.nutrientType));

  if (!macro.length && !micro.length && nutrients.length) {
    return { macro: nutrients, micro: [] };
  }

  return { macro, micro };
}

export async function loadFssaiNutrientMasterList(
  masterService: MasterService,
  pageSize = 1000
): Promise<FssaiNutrientOption[]> {
  const paginationRequest = new PaginationRequest();
  paginationRequest.pageNo = 1;
  paginationRequest.pageSize = pageSize;
  paginationRequest.searchString = '';

  const response = await masterService.getNutrients(paginationRequest);
  return extractFssaiNutrientList(response);
}

export async function loadFssaiNutrientTagsForNutrient(
  masterService: MasterService,
  nutrientId: string | number,
  pageSize = 1000
): Promise<FssaiNutrientTagOption[]> {
  const paginationRequest = new PaginationRequest();
  paginationRequest.pageNo = 1;
  paginationRequest.pageSize = pageSize;
  paginationRequest.searchString = '';
  paginationRequest.parentId = String(nutrientId);

  const response = await masterService.getFssaiNutrientTags(paginationRequest);
  return extractFssaiNutrientTagList(response);
}

export function createFssaiNutrientTagRow(): FssaiNutrientTagRow {
  return {
    nutrientId: null,
    selectedTagIds: [],
    availableTags: [],
    isLoadingTags: false,
  };
}
